import { Backdrop } from '@mui/material'
import React from 'react'
import styled from '@emotion/styled'
import { CloseIcon, Modal, ModalBody, ModalSidebar } from 'src/components/modal'
import { Spinner } from 'src/components/spinner'
import { Colors, Shapes } from 'src/components/theme'
import { Typography } from '@mui/material'
import { SupportEmail } from 'src/components/account/support-email'
import { Mail } from '@emotion-icons/material-outlined/Mail'
import { MarkEmailRead as MailSent } from '@emotion-icons/material-outlined/MarkEmailRead'
import { currentUser } from 'src/atoms/user'
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil'
import { TextField } from 'src/components/text-field'
import { useText } from 'src/utils/hooks'
import { Button } from 'src/components/button'
import { growl, growlError } from 'src/components/growl'
import { userClient } from 'src/clients'
import { MenuItemWrapper } from 'src/layout/toolbar/menu-item'
import { HelpOutline } from '@emotion-icons/material/HelpOutline'

import * as atoms from 'src/atoms/contact-us'

const Sidebar = styled(ModalSidebar)`
  background-color: ${Colors.blueLight};
  display: flex;
  align-items: center;
  justify-content: center;
`

const Circle = styled.div`
  border: 1px solid #cfe5f2;
  background: rgba(0, 0, 0, 0.01);
  width: 181px;
  height: 181px;
  display: flex;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
`

const FromBox = styled.div`
  border-radius: ${Shapes.borderRadius}px;
  background-color: ${Colors.blueLight};
  padding: ${Shapes.spacing[5]}px;
  margin-bottom: ${Shapes.spacing[5]}px;
`

const From: React.FC = () => {
  const user = useRecoilValue(currentUser)
  if (!user) {
    return null
  }
  const from = `${user.firstName} ${user.lastName} - ${user?.email}`
  return (
    <FromBox>
      <Typography variant="text1">
        <span style={{ color: Colors.textBlack, paddingRight: 24 }}>From:</span>
        {from}
      </Typography>
    </FromBox>
  )
}

export const ContactUsModalToogle = () => {
  const [open, setOpen] = useRecoilState(atoms.open)

  const onToggleClick = () => {
    setOpen(!open)
  }

  return (
    <MenuItemWrapper onClick={onToggleClick}>
      <HelpOutline fill="rgba(166, 184, 204, 1)" size={18} />
    </MenuItemWrapper>
  )
}

export const ContactUsModal: React.FC = () => {
  const [open, setOpen] = useRecoilState(atoms.open)

  const [subject, setSubject, resetSubject] = useText('')
  const [body, setBody, resetBody] = useText('')
  const [loading, setLoading] = React.useState(false)
  const [success, setSuccess] = React.useState(false)

  const { state, contents: user } = useRecoilValueLoadable(currentUser)
  const userIsLoaded = state === 'hasValue'
  const formValid = userIsLoaded && (subject || body) && !loading

  const onCloseModal = () => {
    setOpen(false)
  }

  const onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    Promise.resolve()
      .then(async () => {
        setLoading(true)
        const duration = 5000
        await new Promise((resolve) => setTimeout(resolve, 1000))
        try {
          await userClient.contactUs({
            subject,
            message: body,
            from: `${user.firstName} ${user.lastName} <${user.email}>`,
          })
          resetBody()
          resetSubject()
          growl({
            severity: 'success',
            title: 'Message received!',
            text: "Thanks for contacting us.  We'll get back to you soon.",
            autoHideDuration: duration,
          })
          setSuccess(true)
          setTimeout(() => {
            setSuccess(false)
          }, duration)
        } catch (error: any) {
          growlError(error, 'Error sending contact message')
        }

        setLoading(false)
      })
      .catch((e) => growlError(e, 'Error sending contact message'))
  }

  const size = 42
  const icon = loading ? (
    <Spinner />
  ) : success ? (
    <MailSent size={size} color={Colors.greenDark} />
  ) : (
    <Mail size={size} color={Colors.textMedium} />
  )

  return (
    <Modal
      open={open}
      onClose={onCloseModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 200 }}>
      <>
        <CloseIcon style={{ top: 10 }} onClick={onCloseModal} />
        <Sidebar>
          <Circle>
            <Circle style={{ width: 91, height: 91, backgroundColor: 'white', borderColor: 'white' }}>
              <Circle style={{ width: 81, height: 81, backgroundColor: 'white', borderColor: '#E6EDF4' }}>
                {icon}
              </Circle>
            </Circle>
          </Circle>
        </Sidebar>
        <ModalBody style={{ display: 'block', backgroundColor: 'white' }}>
          <Typography variant="header1" color="black" style={{ marginBottom: Shapes.spacing[3] }}>
            Contact Us
          </Typography>
          <Typography variant="text2" style={{ marginBottom: Shapes.spacing[2] }}>
            Reach out to us with questions or feedback, we’d love to hear from you!
          </Typography>
          <Typography variant="text2" style={{ marginBottom: Shapes.spacing[5] }}>
            Simply use the message form below or email us at <SupportEmail /> and we’ll get back to you asap.
          </Typography>
          {userIsLoaded && <From />}
          <form onSubmit={onSubmit}>
            <TextField
              variant="big"
              fullWidth
              label="Subject"
              autoFocus
              value={subject}
              onChange={setSubject}
              style={{ marginBottom: Shapes.spacing[2] }}
            />
            <TextField
              variant="big"
              fullWidth
              label="Message"
              placeholder="Message..."
              multiline
              rows={10}
              value={body}
              onChange={setBody}
              style={{ marginBottom: Shapes.spacing[5] }}
            />
            <Button
              variant="contained"
              color="primary"
              disabled={!formValid}
              type="submit"
              style={{ width: 150, textTransform: 'none' }}>
              Send
            </Button>
          </form>
        </ModalBody>
      </>
    </Modal>
  )
}
